// useTrackerData.js
//
// Shared scaffolding hook for the new tracker apps introduced in Build 24
// (Incidents, Injuries, Maintenance Requests, SOPs). Each app needs the same
// auth + user-profile + applications listener boilerplate; this hook
// consolidates it so the per-app files stay focused on domain logic.
//
// Returns: { user, userProfile, authLoading, applications, role, isAdmin, isTester }
//
// IMPORTANT: this hook is for new (Build 24+) apps. The existing Defect Tracker
// and Feature Requests apps were intentionally NOT refactored to use this
// (Build 23 decision — avoid touching working production apps).

import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc, collection, query, orderBy, onSnapshot
} from 'firebase/firestore';
import { auth, db } from '../firebase';

export function useTrackerData() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [applications, setApplications] = useState([]);

  // Auth + profile
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile({ id: snap.id, ...snap.data() });
          } else {
            setUserProfile(null);
          }
          setAuthLoading(false);
        });
        return () => profileUnsub();
      } else {
        setUserProfile(null);
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Applications listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, (snap) => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  const role = userProfile?.role || null;
  const isAdmin = role === 'Admin';
  const isTester = role === 'Tester/QA';

  return { user, userProfile, authLoading, applications, role, isAdmin, isTester };
}
